/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {createJob} from '@ion/core/src/services/Connect';
import {
  CreateJob,
  Devices,
  DownloadJob,
  Entity,
  ErrorText
} from '@ion/core/src/types/entities';
import {AsyncState} from '@/types/redux';
import {errorOptions, successAddOptions} from '../../prompts/constants';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {getJobErrorText} from './helpers';

// Name of this reducer/state.
export const NAME                   = 'jobCreate';

// Show Job creation editor.
export const SHOW_JOB_CREATE_EDITOR = 'SHOW_JOB_CREATE_EDITOR';

// Actions during Job Creation.
export const JOB_CREATE_REQUEST          = 'JOB_CREATE_REQUEST';
export const JOB_CREATE_RESPONSE         = 'JOB_CREATE_RESPONSE';
export const JOB_CREATE_ERROR            = 'JOB_CREATE_ERROR';
export const JOB_CREATE_SHOW_ADD_DEVICES = 'JOB_CREATE_SHOW_ADD_DEVICES';

// Dispatches an action to show or hide the Job clone/create editor
export const showJobCreateEditor = (
  devices: Devices = null,
  job: DownloadJob = null,
  show: boolean = false
) => ({
  type : SHOW_JOB_CREATE_EDITOR, devices, job, show
});

export const showAddDevices = (showAddDevices: boolean = true) => ({
  type : JOB_CREATE_SHOW_ADD_DEVICES, showAddDevices
});

const formatErrorText = (errorText: string | ErrorText): string => {
  if (errorText?.error) {
    return errorText?.error?.description;
  }
  const pos = errorText.indexOf('-');
  return pos >= 0 ? errorText.substring(pos + 1).trim() : errorText;
};

/**
 * Invoked when a new Job is to be created, delegating to the downloadJob
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addJob = (job: CreateJob): Function => {
  return async(dispatch: Function): Promise<void> => {
    dispatch({type: JOB_CREATE_REQUEST, job});
    try {
      const created = await createJob(job);
      dispatch({type: JOB_CREATE_RESPONSE, job: created});
      dispatch(showJobCreateEditor());
      dispatch(toggleUserPrompt({
        actionLabel   : translate('common:dismiss'),
        message       : translate('jobcreate:errors.jobStatus'),
        translateText : false
      }));
    } catch (err) {
      dispatch({type: JOB_CREATE_ERROR, error: err});
      dispatch(toggleUserPrompt({
        ...errorOptions,
        actionLabel   : translate('common:ok'),
        message       : formatErrorText(getJobErrorText(err)),
        translateText : false
      }));
      error(err);
      throw err;
    }
  };
};

/**
 * REDUCERS
 */
export interface JobCreateState extends AsyncState {
  selectedDevices: Devices | null;
  selectedJob: DownloadJob | null;
  result: DownloadJob | null;
  isEditing: boolean;
  showAddDevices?: boolean;
};

// defines the initial state for the reducer ...
export const initialState: JobCreateState = {
  isEditing       : false,
  selectedDevices : null,
  selectedJob     : null,
  loading         : false,
  error           : null,
  result          : null,
  showAddDevices  : false
};

// defines this reducers reducer functions ...
const reducers = {
  [SHOW_JOB_CREATE_EDITOR] : (draft: JobCreateState, {devices, job, show}) => {
    draft.selectedDevices  = show ? devices : null;
    draft.selectedJob      = show ? job : null;
    draft.isEditing        = show;
  },
  [JOB_CREATE_REQUEST] : (draft: JobCreateState) => {
    draft.loading = true;
    draft.result  = draft.error = null;
  },
  [JOB_CREATE_RESPONSE] : (draft: JobCreateState, {job}) => {
    draft.loading = false;
    draft.result  = job;
  },
  [JOB_CREATE_ERROR] : (draft: JobCreateState, {error}: AsyncState) => {
    draft.loading = false;
    draft.error   = error;
  },
  [JOB_CREATE_SHOW_ADD_DEVICES] : (draft: JobCreateState, {showAddDevices}) => {
    draft.showAddDevices = showAddDevices;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: JobCreateState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
